import * as auth from 'firebase/auth'
import React, { useContext } from 'react'
import { Language } from '../../../pure-js/types/Antiloop'
// Removed SubeventType import as it's no longer needed
import {
  Activity,
  DanceRoles,
  EnrollmentPaymentStatus,
  EnrollmentWithPayment,
  PayFor,
  PaymentMethod
} from '../../../pure-js/types/GrooverTypes'
import { User } from '../../../pure-js/types/User'
import { StorageKey } from './useLocalStorageItemHelper'
import { useSessionState } from './useSessionState'
import { useStorageItem } from './useStorageItem'
import {
  DiscountPricingConfig,
  UsedContinuationDiscountActivity,
  UserEnrolledActivityPeriod
} from '../../../pure-js/types/DiscountTypes'
import { DiscountCode } from '../../../pure-js/types/DiscountCodeTypes'
import { getDefaultLanguageCode } from './useDefaultLanguage'

export type EarlyBirdBookingData = {
  price: number
  bookingId: string
  pricingConfigId?: string
}

export type State = {
  user: User
  firebaseUser?: auth.User
  isRegistrationCompleted: boolean
  activeRegisterStep: number
  name: string
  phoneNumber: string
  email: string
  role: DanceRoles | null
  partnerName?: string
  partnerPhoneNumber?: string
  partnerEmail?: string
  partnerRole: DanceRoles | null
  finalPrice: string
  hasFormErrors: boolean
  selectedActivities: Activity[]
  isMainUserStudent: boolean
  isPartnerStudent: boolean
  isMainUserSibling: boolean
  isPartnerSibling: boolean
  isMainUserSenior: boolean
  isPartnerSenior: boolean
  mainUserActivePeriods: string[]
  partnerUserActivePeriods: string[]
  mainUserEnrolledActivityIds: string[]
  mainUserEnrolledPeriodIds: string[]
  mainUserEnrolledActivityPeriods: UserEnrolledActivityPeriod[]
  mainUsedContinuationDiscountIds: string[]
  mainUsedContinuationDiscountActivities: UsedContinuationDiscountActivity[]
  partnerUserEnrolledActivityIds: string[]
  partnerUserEnrolledPeriodIds: string[]
  partnerUserEnrolledActivityPeriods: UserEnrolledActivityPeriod[]
  partnerUsedContinuationDiscountIds: string[]
  partnerUsedContinuationDiscountActivities: UsedContinuationDiscountActivity[]
  enrollmentId: string
  paymentMethod?: PaymentMethod
  isPaid: boolean
  paymentStatus: EnrollmentPaymentStatus
  selectedLanguage: Language
  isRegistrationSubmitted: boolean
  obfuscatedEnrollment: EnrollmentWithPayment
  mainUserSsn: string
  partnerUserSsn: string
  mainUserAddress: string
  partnerUserAddress: string
  enrollmentComment: string
  registeredPartners: Record<string, string>
  individualFlow: boolean
  payFor: PayFor
  discountType: DiscountPricingConfig['registrationType']
  discountRegion: string
  earlyBirdBooking: Record<string, EarlyBirdBookingData>
  singleEventQuantity?: number
  discountCode?: DiscountCode
  childrenInput: Record<string, { name: string; birthYear: string }>
}

export const DEFAULT_STATE: State = {
  user: { id: '' } as User,
  activeRegisterStep: 0,
  isRegistrationCompleted: false,
  name: '',
  phoneNumber: '',
  email: '',
  role: null,
  partnerName: '',
  partnerPhoneNumber: '',
  partnerEmail: '',
  partnerRole: null,
  finalPrice: '',
  hasFormErrors: false,
  selectedActivities: [],
  isMainUserStudent: false,
  isPartnerStudent: false,
  isMainUserSibling: false,
  isPartnerSibling: false,
  isMainUserSenior: false,
  isPartnerSenior: false,
  mainUserActivePeriods: [],
  partnerUserActivePeriods: [],
  mainUserEnrolledActivityIds: [],
  mainUserEnrolledPeriodIds: [],
  mainUserEnrolledActivityPeriods: [],
  mainUsedContinuationDiscountIds: [],
  mainUsedContinuationDiscountActivities: [],
  partnerUserEnrolledActivityIds: [],
  partnerUserEnrolledPeriodIds: [],
  partnerUserEnrolledActivityPeriods: [],
  partnerUsedContinuationDiscountIds: [],
  partnerUsedContinuationDiscountActivities: [],
  enrollmentId: '',
  paymentMethod: undefined,
  isPaid: false,
  paymentStatus: EnrollmentPaymentStatus.Unpaid,
  selectedLanguage: getDefaultLanguageCode(),
  isRegistrationSubmitted: false,
  obfuscatedEnrollment: {} as EnrollmentWithPayment,
  mainUserSsn: '',
  partnerUserSsn: '',
  mainUserAddress: '',
  partnerUserAddress: '',
  enrollmentComment: '',
  registeredPartners: {},
  individualFlow: true,
  payFor: PayFor.Both,
  discountType: 'single',
  discountRegion: '',
  earlyBirdBooking: {},
  singleEventQuantity: 1,
  discountCode: undefined,
  childrenInput: {}
}

export type AppContext = {
  state: State
  setState: (state: State) => unknown
}
export const Context = React.createContext<AppContext>({
  state: DEFAULT_STATE,
  setState: () => ({})
})

const IGNORED_KEYS: Array<keyof State> = []

export const useAppStateContext = () => {
  const { storageItem: state, setStorageItem: setState } = useStorageItem(StorageKey.STATE, DEFAULT_STATE, {
    ignoredKeys: IGNORED_KEYS
  })

  const session = useSessionState(state)

  return { state: { ...state, ...session }, setState }
}

export default function useAppState() {
  return useContext(Context)
}

export const useUserId = () => useAppState().state.user.id
